/**
 * Fetches interview plan defaults for pre-filling the scheduling wizard.
 * GET /api_ats/v1/ats_interviews/new
 *
 * Used when scheduling from an interview plan (not adhoc).
 * Returns interviewer, duration, location, calendar invite templates, etc.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/request';

export type InterviewPlanParams = {
  stageId: number;
  stageActivityId?: number;
  contactId?: number;
};

async function fetchInterviewPlan(
  params: InterviewPlanParams
): Promise<Record<string, unknown>> {
  // Default unwrapData=true handles the `{ data: { interview: {...}, ... } }`
  // envelope the runtime returns; for endpoints that omit the outer
  // `data` key it falls through unchanged.
  const raw = await api.get<Record<string, unknown>>('ats_interviews/new', {
    params: {
      ats_job_stage_id: params.stageId,
      ...(params.stageActivityId
        ? { ats_job_stage_action_id: params.stageActivityId }
        : {}),
      ...(params.contactId ? { contact_id: params.contactId } : {}),
    },
  });
  // The Rails view nests the interview-config fields under `interview`
  // (see app/views/api_ats/v1/ats_interviews/_interview_config.json.jbuilder):
  //   { interview: { location, duration, kind, ... }, meeting_rooms_enabled,
  //     shared_calendar_enabled, zoom_status, can_fetch_meeting_rooms }
  // Flatten the nested object up so downstream code can read plan.location
  // etc. directly. None of the top-level keys collide with interview keys.
  const interview = raw.interview as Record<string, unknown> | undefined;
  if (interview && typeof interview === 'object') {
    return { ...raw, ...interview };
  }
  return raw;
}

export function useInterviewPlan(
  params: InterviewPlanParams,
  options?: { enabled?: boolean }
): UseQueryResult<Record<string, unknown>, Error> {
  return useQuery({
    queryKey: [
      'schedule-interview',
      'plan',
      params.stageId,
      params.stageActivityId,
      params.contactId,
    ],
    queryFn: () => fetchInterviewPlan(params),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}
